import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';

import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryPromoted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { BuyersPremium } from '@/types/BuyersPremium';
import { WonItemInfo } from '@/types/WonItem';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { calculateBuyersPremium } from '@/utils/calculateBuyersPremium';
import { getImageUrl, getItemUrlFromSlug, getSimilarItemsUrl } from '@/utils/urls';

export type WonItemRowProps = {
    buyersPremium?: BuyersPremium;
    item: WonItemInfo;
    won: boolean;
};

const WonItemRow = ({ buyersPremium, item, won }: WonItemRowProps) => {
    const { formatMessage } = useIntl();
    const trackWonItemCardClicked = useTrackItemCardClicked();
    const promoted = useAppSelector((state) => getItemSummaryPromoted(state, item.itemId));

    const calculateItemTotal = (hammerPrice: number) => {
        if (!buyersPremium) {
            return hammerPrice;
        }
        const { amount: premiumAmount } = calculateBuyersPremium(hammerPrice, buyersPremium);
        return hammerPrice + premiumAmount;
    };

    const { catalogId, currency = 'USD', hammerPrice, itemId, lotNumber, sellerId, slugWithLocation, title } = item;
    const itemHref = getItemUrlFromSlug(itemId, slugWithLocation);
    const similarItemUrl = getSimilarItemsUrl(title);
    //TODO: we need to replace this "imageNumber: 1" this is not necessarily the first image
    const imageUrl = getImageUrl({
        catalogId,
        imageNumber: 1,
        itemId,
        sellerId,
        width: 150,
    });

    const handleItemClick = useCallback(() => {
        trackWonItemCardClicked({
            catalogId,
            itemId,
            promoted,
            source: ItemCardSource.WonItemsPage,
        });
    }, [catalogId, itemId, promoted, trackWonItemCardClicked]);

    return (
        <Container>
            <ImageCopyContainer>
                <ImageContainer>
                    <RouteLink
                        onClick={handleItemClick}
                        to={itemHref}
                    >
                        <img
                            alt={title}
                            src={imageUrl}
                        />
                    </RouteLink>
                </ImageContainer>
                <CopyContainer>
                    <TitleLink
                        onClick={handleItemClick}
                        to={itemHref}
                    >
                        <BodyPrimary>{title}</BodyPrimary>
                    </TitleLink>
                    {lotNumber && (
                        <StyledBodySecondary color="secondary">
                            <FormattedMessage
                                id="item_page_lot_number"
                                values={{ number: lotNumber }}
                            />
                        </StyledBodySecondary>
                    )}
                    {hammerPrice > 0 && (
                        <StyledBodySecondary color="secondary">
                            <FormattedCurrency
                                sourceCurrency={currency}
                                value={calculateItemTotal(hammerPrice)}
                            />
                        </StyledBodySecondary>
                    )}
                    {!won && (
                        <SimilarLink to={similarItemUrl}>
                            <FormattedMessage id="view_similar_items" />
                        </SimilarLink>
                    )}
                </CopyContainer>
            </ImageCopyContainer>
            {!won && (
                <SimilarButtonWrapper>
                    <div>
                        <RouteButton to={similarItemUrl}>{formatMessage({ id: 'view_similar_items' })}</RouteButton>
                    </div>
                </SimilarButtonWrapper>
            )}
        </Container>
    );
};

export default WonItemRow;

const Container = styled.div`
    display: flex;
    margin-bottom: 32px;
    &:last-child {
        margin-bottom: 0;
    }
`;
const ImageCopyContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
`;
const ImageContainer = styled.div`
    border: solid 2px ${({ theme }) => theme.colors.grey400};
    margin-right: 16px;
    max-width: 96px;
    max-height: 96px;
    min-height: 96px;
    min-width: 96px;
    a {
        width: 92px;
        height: 92px;
        display: flex;
        justify-content: center;
        align-items: center;
        img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        }
    }
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-right: 16px;
        max-width: 76px;
        max-height: 76px;
        min-height: 76px;
        min-width: 76px;
        a {
            width: 72px;
            height: 72px;
            display: flex;
            justify-content: center;
            align-items: center;
            img {
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
            }
        }
    }
`;
const CopyContainer = styled.div`
    margin-bottom: 10px;
`;
const TitleLink = styled(RouteLink)`
    margin-bottom: 10px;
    text-decoration: none;
    color: ${({ theme }) => theme.colors.black};
`;
const SimilarLink = styled(RouteLink)`
    display: none;
    margin-top: 20px;
    font-size: 13px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: block;
    }
`;
const SimilarButtonWrapper = styled.div`
    width: 50%;
    display: flex;
    justify-content: flex-end;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;
const StyledBodySecondary = styled(BodySecondary)`
    display: block;
`;
